import { reportError } from "utils/diagnostics";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { PrimaryButton } from "components/common/Buttons";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  connectMissionControl,
  getMissionControlStatus,
  missionControlAddress,
  missionControlErrorMessage,
  vpnFailureMessage,
} from "./mission-control.api";
import { MissionControlHelp } from "./mission-control-help.component";

const FRAME_WAIT_MS = 60000;

export default function LaunchMissionControlPage() {
  const navigate = useNavigate();
  const { sn: serialNumber } = useParams();
  const requestRef = useRef(null);
  const [status, setStatus] = useState();
  const [requestError, setRequestError] = useState("");
  const [statusRefreshError, setStatusRefreshError] = useState("");
  const [isChecking, setIsChecking] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [frameUrl, setFrameUrl] = useState("");
  const [frameVersion, setFrameVersion] = useState(0);
  const [isFrameLoading, setIsFrameLoading] = useState(false);
  const [frameWaitExpired, setFrameWaitExpired] = useState(false);

  const openFrame = (result) => {
    const address = missionControlAddress(result);
    if (result?.vpnStatus !== "Connected" || !address) return;
    setFrameUrl(address.href);
    // Remount even when reconnect returns the same address.
    setFrameVersion((version) => version + 1);
    setIsFrameLoading(true);
    setFrameWaitExpired(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    requestRef.current = controller;
    setStatus(undefined);
    setFrameUrl("");
    setIsFrameLoading(false);
    setFrameWaitExpired(false);
    setIsReconnecting(false);
    setRequestError("");
    setStatusRefreshError("");
    setIsChecking(true);
    getMissionControlStatus(serialNumber, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setStatus(result);
        if (result.vpnStatus === "Connected" && missionControlAddress(result)) openFrame(result);
        else setRequestError(vpnFailureMessage(result));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        reportError("mission_control.status", error);
        setRequestError(missionControlErrorMessage(error));
      })
      .finally(() => {
        if (requestRef.current === controller) {
          requestRef.current = null;
          setIsChecking(false);
        }
      });
    return () => {
      controller.abort();
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [serialNumber]);

  useEffect(() => {
    if (!frameUrl || isReconnecting) return undefined;
    const controller = new AbortController();
    // Refresh diagnostics without navigating or remounting the embedded page.
    const timer = setInterval(() => {
      getMissionControlStatus(serialNumber, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setStatus(result);
          setStatusRefreshError(result.vpnStatus === "Connected" ? "" : vpnFailureMessage(result));
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          reportError("mission_control.status_refresh", error);
          setStatusRefreshError(missionControlErrorMessage(error));
        });
    }, 60000);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [serialNumber, frameUrl, isReconnecting]);

  useEffect(() => {
    if (!isFrameLoading) return undefined;
    const timer = setTimeout(() => {
      setIsFrameLoading(false);
      setFrameWaitExpired(true);
    }, FRAME_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isFrameLoading, frameVersion]);

  const handleReconnect = async () => {
    if (isReconnecting) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setRequestError("");
    setStatusRefreshError("");
    setIsChecking(false);
    setIsReconnecting(true);
    setIsFrameLoading(false);
    setFrameWaitExpired(false);
    setFrameUrl("");
    try {
      const result = await connectMissionControl(serialNumber, {
        reconnect: true,
        signal: controller.signal,
        onStatus: (value) => {
          if (!controller.signal.aborted) setStatus(value);
        },
      });
      if (controller.signal.aborted) return;
      setStatus(result);
      openFrame(result);
    } catch (error) {
      if (controller.signal.aborted) return;
      reportError("mission_control.reconnect", error);
      setRequestError(missionControlErrorMessage(error));
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setIsReconnecting(false);
      }
    }
  };

  const cancelReconnect = () => {
    requestRef.current?.abort();
    requestRef.current = null;
    setIsReconnecting(false);
    setRequestError("Stopped waiting for the VPN connection. You can retry when ready.");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, pt: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">Mission Control — {serialNumber}</Typography>
        <PrimaryButton onClick={handleReconnect} disabled={isChecking || isReconnecting}>
          {isReconnecting ? "Reconnecting..." : "Re-Connect"}
        </PrimaryButton>
        {isReconnecting && <Button onClick={cancelReconnect}>Cancel</Button>}
        <Button onClick={() => navigate(`/devices/${serialNumber}`)}>Back to device</Button>
      </Stack>
      {(isChecking || isReconnecting) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }} role="status">
          <CircularProgress size={20} />
          <Typography>
            {isReconnecting ? "Waiting for the VPN connection (up to 5 minutes)." : "Checking VPN status..."}
          </Typography>
        </Stack>
      )}
      {(requestError || statusRefreshError) && (
        <Alert severity="error" sx={{ m: 2 }}>
          {requestError || statusRefreshError}
        </Alert>
      )}
      {frameWaitExpired && (
        <Alert severity="warning" sx={{ m: 2 }}>
          Mission Control is taking longer than expected to open. Try Re-Connect or Open in new tab.
        </Alert>
      )}
      <MissionControlHelp status={frameUrl ? { ...status, mcUrl: frameUrl } : status} />
      {frameUrl && (
        <>
          {isFrameLoading && (
            <Typography role="status" sx={{ px: 2 }}>
              Opening Mission Control...
            </Typography>
          )}
          <iframe
            key={`${serialNumber}-${frameVersion}`}
            title="Mission Control"
            src={frameUrl}
            style={{ width: "100%", flex: "1 1 auto", minHeight: 500, border: 0 }}
            // A load event also fires for browser error pages. It is not proof
            // that LuCI loaded or that the certificate was accepted.
            onLoad={() => {
              setIsFrameLoading(false);
              setFrameWaitExpired(false);
            }}
          />
        </>
      )}
    </Box>
  );
}
